import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { API_BASE_URL } from '../config';

// Base API URL
const API_URL = `${API_BASE_URL}/api`;

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    Accept: 'application/json'
  },
  withCredentials: true
});

// Request interceptor for adding auth token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

let refreshPromise = null;
// Response interceptor for handling token refresh
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle network errors (status code 0)
    if (!error.response || error.response.status === 0) {
      console.error('Network error detected:', error.message);
      return Promise.reject(error);
    }
    
    if (originalRequest.url === '/auth/refresh-token') {
      return Promise.reject(error);
    }
    
    const hasToken = !!localStorage.getItem('token');
    const isAuthEndpoint = originalRequest?.url?.startsWith('/auth/');
    const hasAuthHeader = !!originalRequest?.headers?.Authorization;
    // Only refresh when we already have a token and the failing request was authenticated
    if (error.response?.status === 401 && !originalRequest._retry && hasToken && !isAuthEndpoint && hasAuthHeader) {
      originalRequest._retry = true;
      try {
        const existingToken = localStorage.getItem('token');
        let existingUserId = null;
        if (existingToken) {
          try {
            existingUserId = jwtDecode(existingToken)?.UserId ?? null;
          } catch {
            existingUserId = null;
          }
        }

        if (!refreshPromise) {
          refreshPromise = apiClient.post('/auth/refresh-token', {}, { withCredentials: true });
          window.__refreshPromise = refreshPromise;
        }
        const response = await refreshPromise;
        refreshPromise = null;
        window.__refreshPromise = null;
        if (response.data && response.data.token) {
          let refreshedUserId = response.data.userId ?? response.data.UserId ?? null;
          if (!refreshedUserId) {
            try {
              refreshedUserId = jwtDecode(response.data.token)?.UserId ?? null;
            } catch {
              refreshedUserId = null;
            }
          }

          if (existingUserId && refreshedUserId && String(existingUserId) !== String(refreshedUserId)) {
            localStorage.removeItem('token');
            try {
              window.dispatchEvent(new CustomEvent('authTokenUpdated', { detail: { token: null } }));
            } catch {
            }
            if (window.location.pathname !== '/login' &&
                window.location.pathname !== '/reset-password' &&
                window.location.pathname !== '/forgot-password') {
              window.location.href = '/login';
            }
            return Promise.reject(error);
          }

          localStorage.setItem('token', response.data.token);
          try {
            window.dispatchEvent(new CustomEvent('authTokenUpdated', {
              detail: {
                token: response.data.token,
                user: {
                  userId: response.data.userId ?? response.data.UserId ?? refreshedUserId ?? null,
                  username: response.data.username ?? response.data.Username ?? null,
                  roles: response.data.roles ?? response.data.Roles ?? null,
                  permissions: response.data.permissions ?? response.data.Permissions ?? null,
                }
              }
            }));
          } catch {
          }
          apiClient.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
          originalRequest.headers['Authorization'] = `Bearer ${response.data.token}`;
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        localStorage.removeItem('token');
        try {
          window.dispatchEvent(new CustomEvent('authTokenUpdated', { detail: { token: null } }));
        } catch {
        }
        refreshPromise = null;
        window.__refreshPromise = null;
        if (window.location.pathname !== '/login' &&
            window.location.pathname !== '/reset-password' &&
            window.location.pathname !== '/forgot-password') {
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to handle API errors
const handleApiError = (error) => {
  // Log the error
  console.error('API Error:', error);
  
  // Extract the error message
  let errorMessage = 'An unexpected error occurred';
  
  if (error.response) {
    // The request was made and the server responded with an error status
    errorMessage = error.response.data?.message || `Error: ${error.response.status}`;
  } else if (error.request) {
    // The request was made but no response was received
    errorMessage = 'No response received from server';
  } else {
    // Something happened in setting up the request
    errorMessage = error.message;
  }
  
  return errorMessage;
};

// Export both named exports and default export
export { apiClient, handleApiError };
export default apiClient;
